// src/pages/Monitor.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Activity, Plus, RefreshCw, Server, Globe, Wifi, Lock, Trash2, Edit2,
  Play, AlertTriangle, CheckCircle, XCircle, Clock, BarChart2, Download,
  ChevronDown, ChevronUp, Zap, TrendingUp, AlertCircle, Eye, X
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';

// ─── Costanti ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  up:      { color: '#22c55e', bg: '#22c55e22', label: 'Online',   Icon: CheckCircle },
  down:    { color: '#ef4444', bg: '#ef444422', label: 'Offline',  Icon: XCircle },
  timeout: { color: '#f59e0b', bg: '#f59e0b22', label: 'Timeout',  Icon: Clock },
  error:   { color: '#f59e0b', bg: '#f59e0b22', label: 'Eroare',   Icon: AlertCircle },
  unknown: { color: '#8892a4', bg: '#8892a422', label: 'Necunoscut',Icon: Activity },
  paused:  { color: '#8892a4', bg: '#8892a422', label: 'Pauzat',   Icon: Activity },
};

const TYPE_ICONS = { ping: Wifi, tcp: Server, http: Globe, https: Lock };

// ─── Mini Sparkline SVG ────────────────────────────────────────────────────
function Sparkline({ data, width = 120, height = 36, color = '#4f8ef7' }) {
  if (!data || data.length < 2) {
    return <svg width={width} height={height}><text x={width/2} y={height/2} textAnchor="middle" fill="#8892a4" fontSize="10">—</text></svg>;
  }
  const vals = data.map(d => d.avg_ms || 0).filter(v => v > 0);
  if (vals.length < 2) return <svg width={width} height={height} />;

  const max = Math.max(...vals) || 1;
  const min = Math.min(...vals);
  const range = max - min || 1;
  const pad = 3;

  const points = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (width - pad * 2);
    const y = pad + (1 - (v - min) / range) * (height - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  // Area fill
  const areaPoints = `${pad},${height - pad} ` + points + ` ${width - pad},${height - pad}`;

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Response Time Chart (SVG Canvas) ─────────────────────────────────────
function ResponseChart({ data, targetId, hours, setHours }) {
  const canvasRef = useRef(null);
  const W = 680, H = 200;
  const PAD = { top: 16, right: 16, bottom: 40, left: 56 };

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter(d => d.avg_ms != null);
  }, [data]);

  const maxMs = useMemo(() => Math.max(...chartData.map(d => d.max_ms || d.avg_ms || 0), 100), [chartData]);

  const toX = (i) => PAD.left + (i / Math.max(chartData.length - 1, 1)) * (W - PAD.left - PAD.right);
  const toY = (v) => PAD.top + (1 - v / maxMs) * (H - PAD.top - PAD.bottom);

  const upPct = data?.length > 0
    ? Math.round(data.reduce((s, d) => s + (d.up_count / (d.checks || 1)), 0) / data.length * 100)
    : 100;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Timp Răspuns (ms)</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[1, 3, 6, 12, 24].map(h => (
            <button key={h} onClick={() => setHours(h)}
              style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)', background: hours === h ? 'var(--accent)' : 'transparent', color: hours === h ? 'white' : 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>
              {h}h
            </button>
          ))}
        </div>
      </div>

      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => {
          const y = PAD.top + pct * (H - PAD.top - PAD.bottom);
          const val = Math.round(maxMs * (1 - pct));
          return (
            <g key={pct}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="4,4" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end" fill="var(--text-secondary)" fontSize="10">{val}</text>
            </g>
          );
        })}

        {/* X axis labels */}
        {chartData.length > 1 && [0, 0.25, 0.5, 0.75, 1].map(pct => {
          const idx = Math.floor(pct * (chartData.length - 1));
          const d = chartData[idx];
          if (!d) return null;
          const x = toX(idx);
          const label = new Date(d.minute).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
          return (
            <text key={pct} x={x} y={H - 8} textAnchor="middle" fill="var(--text-secondary)" fontSize="10">{label}</text>
          );
        })}

        {chartData.length > 0 && (() => {
          // Area fill
          const areaPoints = chartData.map((d, i) => `${toX(i)},${toY(d.avg_ms || 0)}`);
          const areaPath = `M ${toX(0)},${toY(chartData[0]?.avg_ms || 0)} ` +
            areaPoints.slice(1).map((p, i) => `L ${p}`).join(' ') +
            ` L ${toX(chartData.length - 1)},${H - PAD.bottom} L ${toX(0)},${H - PAD.bottom} Z`;

          // Line
          const linePath = `M ${areaPoints.join(' L ')}`;

          // Max line
          const maxPoints = chartData.map((d, i) => `${toX(i)},${toY(d.max_ms || d.avg_ms || 0)}`);
          const maxPath = `M ${maxPoints.join(' L ')}`;

          return (
            <>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4f8ef7" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#4f8ef7" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#areaGrad)" />
              <path d={maxPath} fill="none" stroke="#4f8ef744" strokeWidth="1" strokeDasharray="3,3" />
              <path d={linePath} fill="none" stroke="#4f8ef7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {/* Dots pe ultimul punct */}
              {chartData.length > 0 && (
                <circle cx={toX(chartData.length - 1)} cy={toY(chartData[chartData.length - 1]?.avg_ms || 0)} r="4" fill="#4f8ef7" />
              )}
            </>
          );
        })()}

        {/* Down periods - colorate roșu */}
        {data?.filter(d => d.up_count === 0 && d.checks > 0).map((d, i) => {
          const idx = chartData.findIndex(c => c.minute === d.minute);
          if (idx < 0) return null;
          const x = toX(idx);
          return <rect key={i} x={x - 2} y={PAD.top} width={4} height={H - PAD.top - PAD.bottom} fill="#ef444433" />;
        })}
      </svg>

      <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 2, background: '#4f8ef7', display: 'inline-block' }} /> Medie</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 1, background: '#4f8ef744', display: 'inline-block', borderTop: '1px dashed #4f8ef744' }} /> Maxim</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 12, height: 8, background: '#ef444433', display: 'inline-block' }} /> Down</span>
        <span style={{ marginLeft: 'auto', color: upPct >= 99 ? 'var(--success)' : upPct >= 95 ? 'var(--warning)' : 'var(--danger)', fontWeight: 600 }}>
          Uptime: {upPct}%
        </span>
      </div>
    </div>
  );
}

// ─── Uptime Bar (ultimi 90 check-uri vizualizate ca blocuri) ───────────────
function UptimeBar({ targetId }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/monitor/targets/${targetId}/history`, { params: { limit: 90, hours: 24 } })
      .then(r => setHistory(r.data)).catch(() => {});
  }, [targetId]);

  if (history.length === 0) return null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {history.slice(-90).map((h, i) => {
          const color = h.status === 'up' ? '#22c55e' : h.status === 'timeout' ? '#f59e0b' : '#ef4444';
          return (
            <div key={i} title={`${h.status} - ${h.response_ms}ms\n${new Date(h.checked_at).toLocaleString('ro-RO')}`}
              style={{ width: 6, height: 20, borderRadius: 2, background: color, opacity: 0.85 }} />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--text-secondary)' }}>
        <span>24h în urmă</span><span>Acum</span>
      </div>
    </div>
  );
}

// ─── Target Detail Panel ───────────────────────────────────────────────────
function TargetDetail({ target, liveData, onClose }) {
  const [chartData, setChartData] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [hours, setHours] = useState(6);
  const [loadingChart, setLoadingChart] = useState(true);

  const loadChart = useCallback(async () => {
    setLoadingChart(true);
    try {
      const [chart, inc] = await Promise.all([
        api.get(`/monitor/targets/${target.id}/chart`, { params: { hours } }),
        api.get(`/monitor/targets/${target.id}/incidents`)
      ]);
      setChartData(chart.data);
      setIncidents(inc.data);
    } finally { setLoadingChart(false); }
  }, [target.id, hours]);

  useEffect(() => { loadChart(); }, [loadChart]);

  const live = liveData[target.id] || {};
  const status = live.status || target.status || 'unknown';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.Icon;
  const responseMs = live.response_ms ?? target.last_response_ms;
  const uptime = live.uptime_24h ?? target.uptime_24h;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 800, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={22} color={cfg.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{target.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>{target.host}{target.port ? `:${target.port}` : ''}</div>
          </div>
          {/* Live indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, animation: status === 'up' ? 'pulse 2s infinite' : 'none' }} />
            <span style={{ color: cfg.color, fontSize: 13, fontWeight: 600 }}>{cfg.label}</span>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ padding: 24 }}>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Răspuns Curent', value: responseMs != null ? `${responseMs} ms` : '—', color: responseMs < 200 ? 'var(--success)' : responseMs < 1000 ? 'var(--warning)' : 'var(--danger)' },
              { label: 'Uptime 24h', value: uptime != null ? `${uptime?.toFixed(1)}%` : '—', color: uptime >= 99 ? 'var(--success)' : uptime >= 95 ? 'var(--warning)' : 'var(--danger)' },
              { label: 'Medie 24h', value: (live.avg_response_24h ?? target.avg_response_24h) != null ? `${Math.round(live.avg_response_24h ?? target.avg_response_24h)} ms` : '—', color: 'var(--accent)' },
              { label: 'Eșecuri Consecutive', value: live.consecutive_failures ?? target.consecutive_failures ?? 0, color: (live.consecutive_failures ?? target.consecutive_failures ?? 0) > 0 ? 'var(--danger)' : 'var(--success)' },
            ].map(k => (
              <div key={k.label} style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Uptime bar */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>ISTORICUL ULTIMELOR 90 CHECK-URI</div>
            <UptimeBar targetId={target.id} />
          </div>

          {/* Chart */}
          <div className="card" style={{ marginBottom: 20 }}>
            {loadingChart ? (
              <div className="loading-center"><span className="spinner" /></div>
            ) : (
              <ResponseChart data={chartData} targetId={target.id} hours={hours} setHours={setHours} />
            )}
          </div>

          {/* Incidents */}
          {incidents.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Incidente Recente</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.slice(0, 5).map(inc => (
                  <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg-hover)', borderRadius: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: inc.resolved_at ? 'var(--success)' : 'var(--danger)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {new Date(inc.started_at).toLocaleString('ro-RO')}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {inc.resolved_at
                          ? `Rezolvat după ${inc.duration_seconds ? formatDuration(inc.duration_seconds) : '?'}`
                          : <span style={{ color: 'var(--danger)', fontWeight: 600 }}>⚠ Incident activ</span>}
                      </div>
                    </div>
                    <span className={`badge ${inc.resolved_at ? 'badge-success' : 'badge-danger'}`}>
                      {inc.resolved_at ? 'Rezolvat' : 'Activ'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

// ─── Target Card ───────────────────────────────────────────────────────────
function TargetCard({ target, liveData, sparklines, onEdit, onDelete, onCheck, onSelect, hasRole }) {
  const live = liveData[target.id] || {};
  const status = live.status || target.status || 'unknown';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.Icon;
  const TypeIcon = TYPE_ICONS[target.type] || Wifi;
  const responseMs = live.response_ms ?? target.last_response_ms;
  const uptime = live.uptime_24h ?? target.uptime_24h;
  const isFlashing = live.statusChanged;
  const spark = sparklines[target.id] || [];

  return (
    <div className="card" style={{
      border: `1px solid ${status === 'down' || status === 'timeout' ? 'var(--danger)33' : status === 'up' ? 'var(--border)' : 'var(--border)'}`,
      transition: 'border-color 0.3s',
      animation: isFlashing ? 'pulse 0.5s ease 2' : 'none',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 9, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.3s' }}>
          <Icon size={19} color={cfg.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{target.name}</span>
            <span style={{ padding: '1px 6px', borderRadius: 10, background: 'var(--bg-hover)', fontSize: 10, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 3 }}>
              <TypeIcon size={9} />{target.type.toUpperCase()}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {target.host}{target.port ? `:${target.port}` : ''}
          </div>
        </div>
        {/* Live status badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.color, boxShadow: status === 'up' ? `0 0 6px ${cfg.color}` : 'none', animation: status === 'up' ? 'pulse 2.5s infinite' : 'none' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div style={{ background: 'var(--bg-hover)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>RĂSPUNS</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: responseMs == null ? 'var(--text-secondary)' : responseMs < 200 ? 'var(--success)' : responseMs < 1000 ? 'var(--warning)' : 'var(--danger)' }}>
            {responseMs != null ? `${responseMs}ms` : '—'}
          </div>
        </div>
        <div style={{ background: 'var(--bg-hover)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>UPTIME 24H</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: uptime == null ? 'var(--text-secondary)' : uptime >= 99 ? 'var(--success)' : uptime >= 95 ? 'var(--warning)' : 'var(--danger)' }}>
            {uptime != null ? `${uptime?.toFixed(1)}%` : '—'}
          </div>
        </div>
        <div style={{ background: 'var(--bg-hover)', borderRadius: 6, padding: '7px 10px' }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>INTERVAL</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{target.interval_seconds}s</div>
        </div>
      </div>

      {/* Sparkline */}
      <div style={{ marginBottom: 12 }}>
        <Sparkline data={spark} width={300} height={32} color={status === 'up' ? '#22c55e' : status === 'down' ? '#ef4444' : '#8892a4'} />
      </div>

      {/* Last check */}
      {(live.last_checked_at || target.last_checked_at) && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Verificat: {new Date(live.last_checked_at || target.last_checked_at).toLocaleString('ro-RO')}
          {target.vps_name && <span> · {target.vps_name}</span>}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, borderTop: '1px solid var(--border)44', paddingTop: 12 }}>
        <button className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => onSelect(target)}>
          <Eye size={12} /> Detalii
        </button>
        <button className="btn btn-secondary btn-sm" title="Check manual" onClick={() => onCheck(target.id)}>
          <Play size={12} />
        </button>
        <button className="btn-icon btn-sm" title="Editare" onClick={() => onEdit(target)}>
          <Edit2 size={13} />
        </button>
        {hasRole('superadmin', 'admin') && (
          <button className="btn-icon btn-sm" style={{ color: 'var(--danger)' }} title="Șterge" onClick={() => onDelete(target)}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Target Modal (Create/Edit) ────────────────────────────────────────────
function TargetModal({ target, clients, vpsList, onClose, onSave }) {
  const [f, setF] = useState(target || {
    name: '', host: '', type: 'ping', port: '', interval_seconds: 60,
    timeout_ms: 5000, notify_on_down: 1, vps_id: '', client_id: ''
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const PORT_DEFAULTS = { ping: '', tcp: 80, http: 80, https: 443 };

  const save = async () => {
    if (!f.name || !f.host) return toast.error('Nume și host sunt obligatorii');
    setLoading(true);
    try {
      if (target?.id) { await api.put(`/monitor/targets/${target.id}`, f); toast.success('Target actualizat'); }
      else { await api.post('/monitor/targets', f); toast.success('Monitorizare pornită!'); }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header">
          <span className="modal-title">{target?.id ? 'Editare Target' : 'Target Nou'}</span>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Nume *</label>
              <input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="Server Web Principal" />
            </div>
            <div className="form-group">
              <label className="form-label">Host / IP *</label>
              <input className="form-input" value={f.host} onChange={e => set('host', e.target.value)} placeholder="185.120.45.10 sau example.ro" />
            </div>
            <div className="form-group">
              <label className="form-label">Tip Check</label>
              <select className="form-select" value={f.type} onChange={e => { set('type', e.target.value); set('port', PORT_DEFAULTS[e.target.value]); }}>
                <option value="ping">Ping (TCP multi-port)</option>
                <option value="tcp">TCP Connect</option>
                <option value="http">HTTP</option>
                <option value="https">HTTPS</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Port {f.type === 'ping' ? '(auto)' : ''}</label>
              <input className="form-input" type="number" value={f.port || ''} onChange={e => set('port', e.target.value)} placeholder={f.type === 'ping' ? 'Auto' : '80'} disabled={f.type === 'ping'} />
            </div>
            <div className="form-group">
              <label className="form-label">Interval (secunde)</label>
              <select className="form-select" value={f.interval_seconds} onChange={e => set('interval_seconds', parseInt(e.target.value))}>
                {[15, 30, 60, 120, 300, 600].map(s => <option key={s} value={s}>{s}s{s >= 60 ? ` (${s/60}min)` : ''}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Timeout (ms)</label>
              <select className="form-select" value={f.timeout_ms} onChange={e => set('timeout_ms', parseInt(e.target.value))}>
                {[2000, 3000, 5000, 10000, 15000].map(ms => <option key={ms} value={ms}>{ms}ms</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">VPS Asociat</label>
              <select className="form-select" value={f.vps_id || ''} onChange={e => set('vps_id', e.target.value)}>
                <option value="">— Fără VPS —</option>
                {vpsList.map(v => <option key={v.id} value={v.id}>{v.name} ({v.ip_address})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Client</label>
              <select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Fără client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}
              </select>
            </div>
          </div>
          {target?.id && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Status</label>
              <select className="form-select" value={f.is_active ? 1 : 0} onChange={e => set('is_active', parseInt(e.target.value))}>
                <option value={1}>Activ</option>
                <option value={0}>Pauzat</option>
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <span className="spinner" /> : target?.id ? 'Salvare' : <><Activity size={14} /> Pornire Monitorizare</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function Monitor() {
  const [targets, setTargets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveData, setLiveData] = useState({}); // targetId -> { status, response_ms, ... }
  const [sparklines, setSparklines] = useState({}); // targetId -> chart array
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [clients, setClients] = useState([]);
  const [vpsList, setVpsList] = useState([]);
  const [filter, setFilter] = useState('all'); // all | up | down | unknown
  const [importing, setImporting] = useState(false);
  const { hasRole } = useAuth();
  const toast = useToast();

  // WebSocket live updates
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'MONITOR_UPDATE') {
      const { targetId, statusChanged, ...data } = msg.data;
      setLiveData(prev => ({
        ...prev,
        [targetId]: { ...prev[targetId], ...data, statusChanged }
      }));
      // Update sparkline in real-time
      setSparklines(prev => {
        const arr = [...(prev[targetId] || [])];
        arr.push({ avg_ms: data.response_ms, minute: data.last_checked_at });
        return { ...prev, [targetId]: arr.slice(-60) };
      });
      // Flash notification for status changes
      if (statusChanged && (data.status === 'down' || data.status === 'timeout')) {
        toast.error(`⚠ Server DOWN detectat!`);
        loadStats();
      } else if (statusChanged && data.status === 'up') {
        toast.success(`✅ Server restaurat!`);
        loadStats();
      }
    }
  }, []);

  useWebSocket(handleWsMessage);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, c, v] = await Promise.all([
        api.get('/monitor/targets'),
        api.get('/monitor/stats'),
        api.get('/clients'),
        api.get('/hosting/vps'),
      ]);
      setTargets(t.data);
      setStats(s.data);
      setClients(c.data);
      setVpsList(v.data);
    } finally { setLoading(false); }
  }, []);

  const loadStats = async () => {
    try { const { data } = await api.get('/monitor/stats'); setStats(data); } catch {}
  };

  // Load sparklines for all targets
  const loadSparklines = useCallback(async (targetList) => {
    const result = {};
    await Promise.allSettled(
      targetList.map(async t => {
        try {
          const { data } = await api.get(`/monitor/targets/${t.id}/chart`, { params: { hours: 1 } });
          result[t.id] = data;
        } catch {}
      })
    );
    setSparklines(result);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (targets.length) loadSparklines(targets); }, [targets, loadSparklines]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Ștergi monitorizarea pentru "${t.name}"?`)) return;
    try { await api.delete(`/monitor/targets/${t.id}`); toast.success('Șters'); load(); }
    catch { toast.error('Eroare la ștergere'); }
  };

  const handleManualCheck = async (id) => {
    try {
      toast.info('Check manual în curs...');
      const { data } = await api.post(`/monitor/targets/${id}/check`);
      toast.success(`Check: ${data.status} · ${data.response_ms}ms`);
    } catch { toast.error('Eroare check'); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/monitor/import-vps');
      toast.success(data.message);
      load();
    } catch { toast.error('Eroare import'); }
    finally { setImporting(false); }
  };

  const filteredTargets = targets.filter(t => {
    if (filter === 'all') return true;
    const status = liveData[t.id]?.status || t.status || 'unknown';
    if (filter === 'down') return status === 'down' || status === 'timeout';
    return status === filter;
  });

  const downCount = targets.filter(t => {
    const s = liveData[t.id]?.status || t.status;
    return s === 'down' || s === 'timeout';
  }).length;

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="page-title">Monitorizare Live</div>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: downCount > 0 ? 'var(--danger)' : 'var(--success)', boxShadow: `0 0 8px ${downCount > 0 ? 'var(--danger)' : 'var(--success)'}`, animation: 'pulse 2s infinite' }} />
          </div>
          <div className="page-subtitle">
            Monitoring în timp real · {targets.length} target-uri active · WebSocket live
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleImport} disabled={importing}>
            {importing ? <span className="spinner" /> : <Download size={14} />} Import VPS
          </button>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={14} /> Target Nou</button>
        </div>
      </div>

      {/* Stats KPIs */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total', value: stats.total, color: 'var(--accent)', icon: Activity },
            { label: 'Online', value: stats.up, color: 'var(--success)', icon: CheckCircle },
            { label: 'Offline', value: stats.down, color: stats.down > 0 ? 'var(--danger)' : 'var(--text-secondary)', icon: XCircle },
            { label: 'Uptime Med. 24h', value: `${stats.avgUptime}%`, color: stats.avgUptime >= 99 ? 'var(--success)' : stats.avgUptime >= 95 ? 'var(--warning)' : 'var(--danger)', icon: TrendingUp },
            { label: 'Răspuns Med.', value: stats.avgResponse ? `${stats.avgResponse}ms` : '—', color: 'var(--accent)', icon: Zap },
            { label: 'Incidente Deschise', value: stats.openIncidents, color: stats.openIncidents > 0 ? 'var(--danger)' : 'var(--success)', icon: AlertTriangle },
          ].map(k => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="stat-card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <KIcon size={15} color={k.color} />
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
        {[
          { key: 'all', label: `Toate (${targets.length})` },
          { key: 'up', label: `Online (${targets.filter(t => (liveData[t.id]?.status || t.status) === 'up').length})` },
          { key: 'down', label: `Offline (${downCount})`, color: downCount > 0 ? 'var(--danger)' : undefined },
          { key: 'unknown', label: `Necunoscut (${targets.filter(t => !liveData[t.id]?.status && (!t.status || t.status === 'unknown')).length})` },
        ].map(f2 => (
          <button key={f2.key} onClick={() => setFilter(f2.key)}
            style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filter === f2.key ? 'var(--accent)' : 'var(--border)'}`, background: filter === f2.key ? 'var(--accent)' : 'transparent', color: filter === f2.key ? 'white' : f2.color || 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontWeight: filter === f2.key ? 600 : 400 }}>
            {f2.label}
          </button>
        ))}
      </div>

      {/* Targets Grid */}
      {loading ? (
        <div className="loading-center"><span className="spinner" /></div>
      ) : filteredTargets.length === 0 ? (
        <div className="empty-state">
          <Activity size={48} opacity={0.2} style={{ margin: '0 auto 16px', display: 'block' }} />
          <div className="empty-state-title">
            {targets.length === 0 ? 'Niciun target configurat' : 'Niciun target în această categorie'}
          </div>
          {targets.length === 0 && (
            <p style={{ fontSize: 13, marginTop: 8 }}>
              Adaugă un server sau importă VPS-urile existente din modulul Hosting.
            </p>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {filteredTargets.map(t => (
            <TargetCard
              key={t.id}
              target={t}
              liveData={liveData}
              sparklines={sparklines}
              onEdit={item => setModal(item)}
              onDelete={handleDelete}
              onCheck={handleManualCheck}
              onSelect={setSelected}
              hasRole={hasRole}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <TargetModal
          target={modal === 'new' ? null : modal}
          clients={clients}
          vpsList={vpsList}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
      {selected && (
        <TargetDetail
          target={selected}
          liveData={liveData}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
